// apps/client/src/title/camera.ts (title screen, 2026-10-05) — the title camera: a slow turn high above the showcase town while the
// title shows; Play now swoops it down to the town camera's own view (world/camera.ts: facing north at tuning.camera.pitchDeg and
// distance), easing in and out, or with reduced motion holds still and fades through the sky to the same view. Pure, so it is tested
// without WebGL; title/stage.ts applies it every frame. Portrait landing (2026-10-18): titleFrame frames the town for the screen's shape.
import { tuning } from '@whiskerton/content';
import * as THREE from 'three';
import type { TitleMotion, TitleStep } from '../state/slices/title';
import { cameraOffset } from '../world/camera';

/** Where the camera sits round its target: turned `yaw` (radians, 0 = south of it facing north), `pitchDeg` down, `distance` away. */
export interface TitlePose { yaw: number; pitchDeg: number; distance: number }
const O = tuning.title.orbit, TAU = Math.PI * 2;
/** The town camera's view, where the swoop lands and the backdrop rests behind sign-in and the towns list. */
export const END_POSE: TitlePose = { yaw: 0, pitchDeg: tuning.camera.pitchDeg, distance: tuning.camera.distance };

/** The turn, `seconds` after the title came up (reduced motion: still). */
export function orbitPose(seconds: number, reducedMotion: boolean): TitlePose {
  const yaw = (O.startYawDeg * Math.PI) / 180 + (reducedMotion ? 0 : (seconds / O.periodSeconds) * TAU);
  return { yaw, pitchDeg: O.pitchDeg, distance: O.distance };
}
const ease = (u: number) => (u < 0.5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2);   // cubic in-out
/** The swoop `u` (0…1) of the way from `from` to END_POSE: the turn unwinds the short way, the distance falls geometrically (an even
 *  sense of speed from high up to close in) and the pitch steepens to the town camera's. */
export function swoopPose(from: TitlePose, u: number): TitlePose {
  const e = ease(Math.min(1, Math.max(0, u)));
  const turn = Math.atan2(Math.sin(-from.yaw), Math.cos(-from.yaw));
  return { yaw: from.yaw + turn * e, pitchDeg: from.pitchDeg + (END_POSE.pitchDeg - from.pitchDeg) * e,
    distance: from.distance * Math.pow(END_POSE.distance / from.distance, e) };
}
/** What the camera shows at `now` (performance.now ms; `started` = when the title came up), and how much sky veils it (0…1, the
 *  reduced-motion fade). */
export function titleView(s: { step: TitleStep; motion: TitleMotion; at: number | null }, now: number, started: number, reducedMotion: boolean):
  { pose: TitlePose; veil: number } {
  if (s.step === 'done') return { pose: END_POSE, veil: 0 };
  if (s.step === 'title' || s.at === null) return { pose: orbitPose((now - started) / 1000, reducedMotion), veil: 0 };
  const from = orbitPose((s.at - started) / 1000, reducedMotion);
  if (s.motion === 'swoop') return { pose: swoopPose(from, (now - s.at) / tuning.title.swoopMs), veil: 0 };
  const u = Math.min(1, Math.max(0, (now - s.at) / tuning.title.fadeMs));
  return { pose: u < 0.5 ? from : END_POSE, veil: 1 - Math.abs(2 * u - 1) };
}
const offset = new THREE.Vector3(), up = new THREE.Vector3(0, 1, 0);
/** Puts `cam` at `pose` round `target`, looking at it, with the target drawn `shift` of the screen width right of the middle (a number),
 *  or `x` of the width right and `y` of the height below it. */
export function placeCamera(cam: THREE.PerspectiveCamera, target: THREE.Vector3, pose: TitlePose, shift: number | { x: number; y: number } = 0): void {
  const { x, y } = typeof shift === 'number' ? { x: shift, y: 0 } : shift;
  offset.copy(cameraOffset(pose.pitchDeg, pose.distance)).applyAxisAngle(up, pose.yaw);
  cam.position.copy(target).add(offset);
  cam.lookAt(target);
  cam.updateMatrixWorld();
  // The frame slides left and up: the town sits right of the middle and below it.
  if (x > 0 || y > 0) cam.setViewOffset(cam.aspect, 1, -x * cam.aspect, -y, cam.aspect, 1);
  else if (cam.view?.enabled) cam.clearViewOffset();
  else cam.updateProjectionMatrix();
}
/** How far down from the turn toward the town camera's distance `pose` is: 0 up high, 1 at the town camera. */
const descent = (pose: TitlePose) => Math.min(1, Math.max(0, (O.distance - pose.distance) / (O.distance - END_POSE.distance)));
/** Fog near/far: tuning.title.orbitFog up high, the town's own `town` fog at the town camera's distance, in between on the way down. */
export function fogAt(pose: TitlePose, town: readonly [number, number]): [number, number] {
  const k = descent(pose), [n0, f0] = tuning.title.orbitFog;
  return [n0 + (town[0] - n0) * k, f0 + (town[1] - f0) * k];
}
/** Where the town turns on screen: tuning.title.orbit.screenShift right of the middle up high (clear of the title's words), the
 *  middle at the town camera. */
export function screenShiftAt(pose: TitlePose): number { return O.screenShift * (1 - descent(pose)); }

/** The camera's vertical field of view (degrees) and where the town sits on screen (`x` of the width right of the middle, `y` of the
 *  height below it). */
export interface TitleFrame { fov: number; x: number; y: number }
const FOV = tuning.camera.fovDeg, P = O.portrait;
const deg = (r: number) => (r * 180) / Math.PI, half = (d: number) => Math.tan((d * Math.PI) / 360);
/** How the title frames the town on a screen `aspect` (width over height) wide, `pose` of the way down. Landscape: the town camera's
 *  field of view, the town right of the middle, clear of the words on the left (screenShiftAt). Portrait (2026-10-18), a phone held
 *  upright: the view widens to show as much of the town across as a screen `portrait.fitAspect` wide would (no wider than
 *  `portrait.maxFovDeg`), and the town turns `portrait.shiftY` below the middle, between the words at the top and Play now at the
 *  foot. Both ease back to the town camera's own view as the camera comes down. */
export function titleFrame(pose: TitlePose, aspect: number): TitleFrame {
  if (aspect >= 1) return { fov: FOV, x: screenShiftAt(pose), y: 0 };
  const k = 1 - descent(pose), wide = Math.min(P.maxFovDeg, deg(2 * Math.atan((half(FOV) * P.fitAspect) / aspect)));
  return { fov: FOV + (Math.max(FOV, wide) - FOV) * k, x: 0, y: P.shiftY * k };
}
